import { useMemo } from "react";
import {
  EntityFilterBar,
  FilterCategory,
} from "../../../../components/audit/EntityFilterBar";
import { ConfiguredState } from "../../../../models/audit/control";

interface FilterOptions {
  cycles: string[];
  processes: string[];
  controlOwners: string[];
  configuredStates: string[];
}

interface ControlsFilterBarProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  configuredStateFilters: ConfiguredState[];
  onConfiguredStateFiltersChange: (value: ConfiguredState[]) => void;
  cycleFilters: string[];
  onCycleFiltersChange: (value: string[]) => void;
  processFilters: string[];
  onProcessFiltersChange: (value: string[]) => void;
  controlOwnerFilters: string[];
  onControlOwnerFiltersChange: (value: string[]) => void;
  filterOptions: FilterOptions | null | undefined;
  isFiltersOpen: boolean;
  onFiltersOpenChange: (open: boolean) => void;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

export const ControlsFilterBar = ({
  searchTerm,
  onSearchTermChange,
  configuredStateFilters,
  onConfiguredStateFiltersChange,
  cycleFilters,
  onCycleFiltersChange,
  processFilters,
  onProcessFiltersChange,
  controlOwnerFilters,
  onControlOwnerFiltersChange,
  filterOptions,
  isFiltersOpen,
  onFiltersOpenChange,
  activeFilterCount,
  hasActiveFilters,
  onClearFilters,
}: ControlsFilterBarProps) => {
  // Build filter categories from props
  const filterCategories: FilterCategory[] = useMemo(() => {
    const stateOptions = [
      { label: "Configured", value: ConfiguredState.Configured },
      { label: "Not Configured", value: ConfiguredState.NotConfigured },
      { label: "Draft", value: ConfiguredState.Draft },
    ];

    const cycleOptions =
      filterOptions?.cycles.map((cycle) => ({
        label: cycle,
        value: cycle,
      })) ?? [];

    const processOptions =
      filterOptions?.processes.map((process) => ({
        label: process,
        value: process,
      })) ?? [];

    const ownerOptions =
      filterOptions?.controlOwners.map((owner) => ({
        label: owner,
        value: owner,
      })) ?? [];

    const isConfiguredState = (v: string): v is ConfiguredState =>
      Object.values(ConfiguredState).includes(v as never);

    return [
      {
        id: "state",
        label: "State",
        options: stateOptions,
        selectedValues: configuredStateFilters,
        onSelectionChange: (values) =>
          onConfiguredStateFiltersChange(values.filter(isConfiguredState)),
      },
      {
        id: "cycle",
        label: "Cycle",
        options: cycleOptions,
        selectedValues: cycleFilters,
        onSelectionChange: onCycleFiltersChange,
      },
      {
        id: "process",
        label: "Process",
        options: processOptions,
        selectedValues: processFilters,
        onSelectionChange: onProcessFiltersChange,
      },
      {
        id: "owner",
        label: "Owner",
        options: ownerOptions,
        selectedValues: controlOwnerFilters,
        onSelectionChange: onControlOwnerFiltersChange,
      },
    ];
  }, [
    filterOptions,
    configuredStateFilters,
    onConfiguredStateFiltersChange,
    cycleFilters,
    onCycleFiltersChange,
    processFilters,
    onProcessFiltersChange,
    controlOwnerFilters,
    onControlOwnerFiltersChange,
  ]);

  return (
    <EntityFilterBar
      searchTerm={searchTerm}
      onSearchTermChange={onSearchTermChange}
      searchPlaceholder="Search controls by name or description..."
      categories={filterCategories}
      isFiltersOpen={isFiltersOpen}
      onFiltersOpenChange={onFiltersOpenChange}
      activeFilterCount={activeFilterCount}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={onClearFilters}
      entityType="controls"
    />
  );
};
